import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSquashTime } from '@src/hooks/useSquash';
import {
  getInactiveSquashIds,
  getSquashHistory,
  getSquashName,
  log_history,
} from '@src/data/utils';
import {
  PuzzleAccount,
  RecordWithPlaintext,
  useAccount,
  useRecords,
} from '@puzzlehq/sdk';
import { Squash } from '@src/data/types';
import _ from 'lodash';
import dayjs from 'dayjs';
import { RecordStatus } from '@puzzlehq/types';
import { extractAmountFromInputString } from 'jigsaw';
import { useSquashCompletedStore } from '@src/data/-squashStore';

export const SquashGameContext = createContext<SquashHistoryContextType | null>(
  null
);

type SquashHistoryContextType = {
  squashHistory: Squash[] | undefined;
  squashHistoryDesc: Squash[] | undefined;
  loading: boolean;
  activeSquashRecords: RecordWithPlaintext[] | undefined;
  inactiveSquashRecords: RecordWithPlaintext[] | undefined;
  groupedSquashHistory: [string, Squash[]][] | undefined;
  activeSquash: Squash | undefined;
  activeSquashRecord: RecordWithPlaintext | undefined;
  inactiveSquashes: Squash[] | undefined;
  account: PuzzleAccount | undefined;
  gameOver: boolean;
  setGameOver: (gameOver: boolean) => void;
};

export const SquashGameContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { account } = useAccount();
  const { dayStartUnix, timeDeltaSeconds } = useSquashTime();
  const [gameOver, setGameOver] = useState(false);

  useEffect(() => {
    console.log('gameOver', gameOver);
  }, [gameOver]);

  // Fetch and sort all squash records
  const { records: _squashRecords, loading } = useRecords({
    filter: {
      status: 'All',
      programIds: [import.meta.env.VITE_SQUASH_PROGRAM_ID],
    },
  });

  const sortedSquashRecords = _squashRecords
    ?.slice()
    .sort((a, b) => a.height - b.height);

  // Track active squash record state
  const [activeSquashRecord, setActiveSquashRecord] = useState<
    RecordWithPlaintext | undefined
  >();

  const inactiveSquashIds = useMemo(() => {
    const _inactiveSquashIds = getInactiveSquashIds(_squashRecords);
    log_history('_inactiveSquashIds', _inactiveSquashIds);
    return _inactiveSquashIds;
  }, [JSON.stringify(_squashRecords)]);

  // Filter active squashes
  const activeSquashIds = _.uniq(
    sortedSquashRecords
      ?.filter((record) => {
        return !(inactiveSquashIds ?? []).includes(
          record.data.squash_id as string
        );
      })
      .map((r) => r.data.squash_id as string)
  );

  useEffect(() => {
    log_history('activeSquashIds', activeSquashIds);
  }, [JSON.stringify(activeSquashIds)]);

  let activeSquashId: string | undefined = activeSquashIds?.[0];

  // Get all records for ALL inactive squash ids
  const inactiveSquashRecords = useMemo(() => {
    const _inactiveSquashRecords = sortedSquashRecords
      ?.slice()
      .filter(
        (record) =>
          inactiveSquashIds?.includes(record.data.squash_id as string) &&
          [RecordStatus.Pending, RecordStatus.Unspent].includes(record.status)
      );
    log_history('_inactiveSquashRecords', _inactiveSquashRecords);
    return _inactiveSquashRecords;
  }, [JSON.stringify(sortedSquashRecords)]);

  // Get all records for the active squash id
  const activeSquashRecords = useMemo(() => {
    const _activeSquashRecords = sortedSquashRecords
      ?.slice()
      .filter(
        (record) =>
          record.data.squash_id === activeSquashId &&
          activeSquashIds.includes(record.data.squash_id as string)
      );
    log_history('_activeSquashRecords', _activeSquashRecords);
    return _activeSquashRecords;
  }, [JSON.stringify(sortedSquashRecords), JSON.stringify(activeSquashIds)]);

  const squashRecordCount = activeSquashRecords?.length;

  // Process squash history data
  const squashData = useMemo(() => {
    log_history('activeSquashRecords', activeSquashRecords);
    if (activeSquashRecords && dayStartUnix && activeSquashRecords.length) {
      // Get chronological history
      const _squashHistory = getSquashHistory({
        _squashRecords: activeSquashRecords.slice(),
        timeDeltaSeconds,
        startTimeUnix: dayStartUnix,
      });
      const _squashHistoryDesc = _squashHistory.slice().reverse();
      log_history('squashHistoryDesc', _squashHistoryDesc);

      // Group by month and year
      const groupedHistory = _.groupBy(_squashHistory, (item) => {
        return dayjs(item.time).format('MMMM YYYY');
      });

      // Order groups by descending time
      const _orderedGroupedHistory = _.orderBy(
        Object.entries(groupedHistory),
        ([_, items]) => new Date(items[0].time),
        ['desc']
      );

      log_history('_orderedGroupedHistory', _orderedGroupedHistory);
      log_history('_squashHistory', _squashHistory);
      log_history('_squashHistoryDesc', _squashHistoryDesc);

      return { _orderedGroupedHistory, _squashHistory, _squashHistoryDesc };
    }
  }, [squashRecordCount, dayStartUnix, timeDeltaSeconds]);

  const squashHistory = squashData?._squashHistory;
  const squashHistoryDesc = squashData?._squashHistoryDesc;
  const groupedSquashHistory = squashData?._orderedGroupedHistory;

  // Get current active squash
  const activeSquash: Squash | undefined = useMemo(() => {
    // reverse the squash records so that we are more likely to get the latest non-spent record.
    const reversedSquashRecords = activeSquashRecords?.slice().reverse();
    const record: RecordWithPlaintext | undefined = reversedSquashRecords?.find(
      (r) => r.status !== RecordStatus.Spent
    );
    if (record && squashHistory) {
      setActiveSquashRecord(record);
      const activeSquash = squashHistory[squashHistory.length - 1];
      log_history('activeSquash', activeSquash);
      return activeSquash;
    }
  }, [JSON.stringify(activeSquashRecords), JSON.stringify(squashHistory)]);

  // Process inactive squashes
  const inactiveSquashes: Squash[] | undefined = useMemo(() => {
    if (!inactiveSquashRecords) return undefined;
    log_history('inactiveSquashRecords', inactiveSquashRecords);

    const reversedInactiveSquashRecords = inactiveSquashRecords
      ?.slice()
      .reverse();

    // Group records by squash_id
    const groupedRecords = _.groupBy(
      reversedInactiveSquashRecords,
      (record) => record.data.squash_id
    );

    // For each group, find the latest non-spent record
    const inactiveSquashes = Object.values(groupedRecords)
      .map((records) => {
        const lastRecord = records[records.length - 1];
        log_history('lastRecord', lastRecord);
        const lastWaterUnix = extractAmountFromInputString(
          lastRecord.data.last_water
        );
        log_history('lastWaterUnix', lastWaterUnix);

        const squashData: Squash = {
          displayedLevel: 6,
          mood: 'happy',
          onChainLevel: 6,
          squashId: lastRecord.data.squash_id as string,
          kg: 50,
          lastWatered: lastWaterUnix
            ? new Date(lastWaterUnix * 1000)
            : new Date(),
          squashName: getSquashName(lastRecord) ?? '',
          time: new Date(),
        };
        return squashData;
      })
      .filter((squash) => squash !== undefined);
    log_history('inactiveSquashes', inactiveSquashes);

    return inactiveSquashes;
  }, [JSON.stringify(inactiveSquashRecords), JSON.stringify(squashHistory)]);

  const completedSquashIds = useMemo(() => {
    return _.uniq(inactiveSquashes?.map((squash) => squash.squashId) ?? []);
  }, [inactiveSquashes]);

  const { completedCount, setCompletedCount } = useSquashCompletedStore();

  useEffect(() => {
    console.log('in da use effect', {
      completedCount,
      completedSquashIds: completedSquashIds.length,
    });
    if (completedCount < completedSquashIds.length) {
      console.log('setting gameOver to true', {
        completedCount,
        completedSquashIds: completedSquashIds.length,
      });
      setGameOver(true);
      setCompletedCount(completedSquashIds.length);
    }
  }, [completedSquashIds.length, completedCount]);

  return (
    <SquashGameContext.Provider
      value={{
        squashHistory,
        squashHistoryDesc,
        groupedSquashHistory,
        loading,
        activeSquashRecords,
        inactiveSquashRecords,
        activeSquash,
        activeSquashRecord,
        inactiveSquashes,
        account,
        gameOver,
        setGameOver,
      }}
    >
      {children}
    </SquashGameContext.Provider>
  );
};

export const useSquashGameState = () => {
  const ctx = useContext(SquashGameContext);

  return {
    squashHistory: ctx?.squashHistory ?? undefined,
    squashHistoryDesc: ctx?.squashHistoryDesc ?? undefined,
    activeSquashRecords: ctx?.activeSquashRecords ?? undefined,
    groupedSquashHistory: ctx?.groupedSquashHistory ?? undefined,
    loading: ctx?.loading ?? undefined,
    activeSquash: ctx?.activeSquash,
    activeSquashRecord: ctx?.activeSquashRecord,
    account: ctx?.account,
    inactiveSquashes: ctx?.inactiveSquashes,
    gameOver: ctx?.gameOver,
    setGameOver: ctx?.setGameOver,
  };
};
